import React, { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { connect, ConnectedProps } from 'react-redux';
import { getProducts, updateProduct } from '../../../store/MSCommerce/Productos/thunks';
import { getAllOfertasActivas } from '../../../store/MSCommerce/Ofertas/thunks';
import { getOfertaParaProducto, calcularPrecioFinal } from '../../../Services/ofertaUtils';
import { addItem } from '../../../store/Carrito/actions';
import type { CarritoItem } from '../../../store/Carrito/types';
import type { ProductosResponse } from '../../../store/MSCommerce/Productos/types';
import { AppState } from '../../../store';
import { DetailGallery } from './components/DetailGallery';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { LeftOutlined, EditOutlined, CheckOutlined, CloseOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import { isAdmin } from '../../../Services/auth';
import styles from './ProductoDetalle.module.scss';

const QUILL_MODULES = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ header: [2, 3, false] }],
    ['clean'],
  ],
};

const QUILL_FORMATS = ['bold', 'italic', 'underline', 'list', 'bullet', 'header'];

const mapStateToProps = (state: AppState) => ({
  productos: state.productosAction.products,
  ofertas: state.ofertasAction.ofertas,
  carritoItems: state.carritoAction.items,
});

const mapDispatchToProps = { getProducts, updateProduct, getAllOfertasActivas, addItem };
const connector = connect(mapStateToProps, mapDispatchToProps);
type Props = ConnectedProps<typeof connector>;

const ProductoDetalle: React.FC<Props> = ({ getProducts, updateProduct, productos, ofertas, carritoItems, getAllOfertasActivas, addItem }) => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();

  const [editando, setEditando]           = useState(false);
  const [descripcionEdit, setDescEdit]    = useState('');
  const [guardando, setGuardando]         = useState(false);
  const [errorGuardado, setError]         = useState('');

// Efecto 1 — carga datos
useEffect(() => {
  if (!productos || productos.length === 0) getProducts();
  if (getAllOfertasActivas) getAllOfertasActivas();
}, []);

  const producto = productos?.find(p => p.id === id);
// Efecto 2 — scroll al top SOLO cuando el producto ya se encontró
useEffect(() => {
  if (producto) {
    const main = document.querySelector('main');
    if (main) main.scrollTop = 0;
  }
}, [producto?.id]);
  useEffect(() => {
    if (producto?.description) setDescEdit(producto.description);
  }, [producto?.description]);

  if (!producto) return <div className={styles.detalle__loading}>Cargando producto…</div>;

  const sinStock = (producto.stock ?? 0) === 0;
  const admin    = isAdmin();

  const oferta = getOfertaParaProducto(producto.id, null, ofertas ?? []);
  const { precioFinal, badge } = oferta
    ? calcularPrecioFinal(producto.price, oferta)
    : { precioFinal: producto.price, badge: '' };
  const enCarrito = (carritoItems ?? []).find(i => i.productoId === producto.id);
  const alcanzoStockMaximo = !!enCarrito && enCarrito.cantidad >= (producto.stock ?? 0);

  const handleAddToCart = () => {
    if (!addItem) return;
    addItem({
      productoId: producto.id,
      nombre: producto.nombre as string,
      precio: precioFinal,
      precioOriginal: producto.price,
      imagen: producto.imagenes && producto.imagenes.length > 0 ? producto.imagenes[0] : null,
      cantidad: 1,
      stockDisponible: producto.stock ?? 0,
    });
  };

  const handleEditar = () => {
    setDescEdit(producto.description ?? '');
    setError('');
    setEditando(true);
  };

  const handleCancelar = () => {
    setEditando(false);
    setError('');
  };

  const handleGuardar = async () => {
    setGuardando(true);
    setError('');
    try {
      // Manda todo igual excepto la description actualizada
      await updateProduct({
        id,
        nombre:        producto.nombre        ?? null,
        price:         producto.price         ?? null,
        stock:         producto.stock         ?? null,
        status:        producto.status        ?? null,
        description:   descripcionEdit,
        imagenesNuevas: null,
      } as object);
      setEditando(false);
      getProducts(); // refresca el store
    } catch {
      setError('No se pudo guardar. Intenta de nuevo.');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className={styles.detalle}>

      <button className={styles.detalle__back} onClick={() => history.push('/products')}>
        <LeftOutlined /> Volver a productos
      </button>

      <div className={styles.detalle__content}>

        {/* ── Galería ── */}
        <div className={styles.detalle__gallery}>
          <DetailGallery
            imagenes={producto.imagenes ?? []}
            nombre={producto.nombre as string}
          />
        </div>

        {/* ── Info ── */}
        <div className={styles.detalle__info}>

          <p className={styles.detalle__category}>{producto.category}</p>
          <h1 className={styles.detalle__name}>{producto.nombre}</h1>

          <div className={styles.detalle__priceRow}>
            {badge && <span className={styles.detalle__ofertaBadge}>{badge}</span>}
            {oferta && (
              <span className={styles.detalle__precioOriginal}>${producto.price.toFixed(2)}</span>
            )}
            <span className={`${styles.detalle__price} ${sinStock ? styles['detalle__price--disabled'] : ''}`}>
              ${precioFinal.toFixed(2)}
            </span>
            <span className={sinStock
              ? `${styles.detalle__stock} ${styles['detalle__stock--out']}`
              : styles.detalle__stock
            }>
              {sinStock ? 'Agotado' : `${producto.stock} en stock`}
            </span>
          </div>

          <button
            className={styles.detalle__addCartBtn}
            disabled={sinStock || alcanzoStockMaximo}
            onClick={handleAddToCart}
          >
            <ShoppingCartOutlined />
            {sinStock ? 'Agotado' : alcanzoStockMaximo ? 'Sin más stock' : 'Añadir al carrito'}
          </button>

          {sinStock && (
            <div className={styles.detalle__outOfStock}>
              <span style={{ fontSize: 16 }}>🚫</span>
              <div>
                <p className={styles.detalle__outOfStockTitle}>Producto agotado</p>
                <p className={styles.detalle__outOfStockText}>
                  Este producto no está disponible por el momento.
                </p>
              </div>
            </div>
          )}

          <hr className={styles.detalle__divider} />

          {/* ── Descripción ── */}
          <div className={styles.detalle__descSection}>

            <div className={styles.detalle__descHeader}>
              <span className={styles.detalle__descLabel}>Descripción</span>

              {/* Botones solo para admin */}
              {admin && !editando && (
                <button className={styles.detalle__editBtn} onClick={handleEditar}>
                  <EditOutlined /> Editar
                </button>
              )}
              {admin && editando && (
                <div className={styles.detalle__editActions}>
                  <button
                    className={styles.detalle__saveBtn}
                    onClick={handleGuardar}
                    disabled={guardando}
                  >
                    <CheckOutlined /> {guardando ? 'Guardando…' : 'Guardar'}
                  </button>
                  <button
                    className={styles.detalle__cancelBtn}
                    onClick={handleCancelar}
                    disabled={guardando}
                  >
                    <CloseOutlined /> Cancelar
                  </button>
                </div>
              )}
            </div>

            {/* Editor inline — solo admin en modo edición */}
            {admin && editando
              ? (
                <div className={styles.detalle__editorWrap}>
                  <ReactQuill
                    theme="snow"
                    value={descripcionEdit}
                    onChange={setDescEdit}
                    modules={QUILL_MODULES}
                    formats={QUILL_FORMATS}
                  />
                  {errorGuardado && (
                    <p className={styles.detalle__editError}>{errorGuardado}</p>
                  )}
                </div>
              )
              : (
                // Vista renderizada — lo que ven todos
                <div
                  className={styles.detalle__description}
                  dangerouslySetInnerHTML={{ __html: producto.description ?? '' }}
                />
              )
            }

          </div>
        </div>
      </div>
    </div>
  );
};

export default connector(ProductoDetalle);